import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { Icon } from '@ui/Icon';

export type ElementListSearchFieldProps = {
  draft: string;
  onDraftChange: (next: string) => void;
  setSearchInputFocused: (focused: boolean) => void;
  onDismiss: () => void;
  /** Fires on pointerdown outside the field; the hook decides whether to close. */
  onClickOutside: () => void;
  /** Incremented by {@link useElementListSearchField.openSearch} to focus the input. */
  focusRequestId: number;
};

/**
 * Expanded element-list search row (absolute inset in the header column).
 * Draft vs URL sync, dismiss (Esc / close / click-outside), and analytics live in `useElementListSearchField`.
 */
const ElementListSearchField: React.FC<ElementListSearchFieldProps> = ({
  draft,
  onDraftChange,
  setSearchInputFocused,
  onDismiss,
  onClickOutside,
  focusRequestId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Listen on pointerdown so we react before the focused input fires blur — and
  // before any click handler on the close button — letting the hook arbitrate.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const node = containerRef.current;
      if (!node) return;
      if (event.target instanceof Node && node.contains(event.target)) return;
      onClickOutside();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [onClickOutside]);

  // Cmd+F / magnifier while already open: focus + select without remounting.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [focusRequestId]);

  return (
    <Box
      ref={containerRef}
      data-element-list-search=""
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        position: 'absolute',
        left: '6px',
        right: '6px',
        height: '26px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flex: 1,
          minWidth: 0,
          height: '26px',
          backgroundColor: 'white',
          outline: '1px solid',
          outlineColor: 'custom.focus',
          borderRadius: '5px',
          px: '4px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            opacity: 0.6,
          }}
        >
          <Icon category="mui" icon="Search" size={12} />
        </Box>
        <Box
          component="input"
          ref={inputRef}
          autoFocus
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onDraftChange(e.target.value);
          }}
          onFocus={() => setSearchInputFocused(true)}
          onBlur={() => setSearchInputFocused(false)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') onDismiss();
          }}
          placeholder="Search anything"
          sx={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 11,
            fontFamily: 'inherit',
            color: 'rgba(0,0,0,0.8)',
            '&::placeholder': { color: 'rgba(0,0,0,0.4)' },
            padding: 0,
            minWidth: 0,
          }}
        />
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onDismiss}
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          color: 'rgba(0,0,0,0.45)',
          cursor: 'pointer',
          p: 0,
          px: '4px',
          mx: '2px',
          minWidth: 19,

          '&:hover': {
            color: 'rgba(0,0,0,0.75)',
          },
        }}
      >
        <Icon category="mui" icon="Close" size={11} />
      </Box>
    </Box>
  );
};

export default ElementListSearchField;
